import React from 'react';
import { 
    Tv, Smartphone, Video, Briefcase, Globe, Music, 
    Megaphone, TrendingUp, Film, Camera, Play, Star,
    Sparkles, ArrowRight, CheckCircle, Zap, Heart, Users
} from 'lucide-react';

function ServicesSection() {
    const services = [
        { 
            icon: <Tv size={24} />, 
            title: "TV Commercials", 
            color: "from-emerald-500 to-emerald-600",
            description: "High-impact broadcast ads that capture attention",
            features: ["30-60 Second Spots", "National Campaigns", "High Production Value"]
        },
        { 
            icon: <Smartphone size={24} />, 
            title: "Digital Ads", 
            color: "from-green-500 to-green-600",
            description: "Platform-optimized video content for maximum ROI",
            features: ["YouTube Ads", "Social Media Ads", "Display Videos"]
        },
        { 
            icon: <Globe size={24} />, 
            title: "Social Media Content", 
            color: "from-emerald-500 to-green-600",
            description: "Engaging short-form videos for social platforms",
            features: ["Instagram Reels", "TikTok Videos", "Stories & Posts"]
        },
        { 
            icon: <Briefcase size={24} />, 
            title: "Corporate & Documentary Films", 
            color: "from-green-500 to-emerald-600",
            description: "Powerful storytelling for brand narratives",
            features: ["Brand Films", " CSR Stories", "Documentaries"]
        },
        { 
            icon: <Globe size={24} />, 
            title: "Web Content", 
            color: "from-emerald-500 to-teal-600",
            description: "Website videos that convert visitors",
            features: ["Explainer Videos", "Product Demos", "Testimonials"]
        },
        { 
            icon: <Video size={24} />, 
            title: "Training & Learning Videos", 
            color: "from-teal-500 to-emerald-600",
            description: "Educational content that engages learners",
            features: ["E-Learning Modules", "How-to Guides", "Workshop Recordings"]
        },
        { 
            icon: <Music size={24} />, 
            title: "Music Videos", 
            color: "from-emerald-500 to-green-700",
            description: "Creative visual interpretations of music",
            features: ["Artistic Direction", "Choreography", "Location Shoots"]
        },
        { 
            icon: <Megaphone size={24} />, 
            title: "Branding Content", 
            color: "from-green-500 to-teal-600",
            description: "Strategic content that builds brand identity",
            features: ["Brand Stories", "Identity Films", "Campaign Launches"]
        },
        { 
            icon: <Film size={24} />, 
            title: "Talk Shows", 
            color: "from-teal-500 to-emerald-600",
            description: "Professional studio production for engaging discussions",
            features: ["Studio Setup", "Multi-Camera", "Live Recording"]
        },
        { 
            icon: <Camera size={24} />, 
            title: "Promotional Videos", 
            color: "from-emerald-500 to-green-600",
            description: "Compelling videos that drive action",
            features: ["Product Launches", "Event Promos", "Offer Videos"]
        },
        { 
            icon: <TrendingUp size={24} />, 
            title: "Media Planning & Marketing", 
            color: "from-green-500 to-emerald-600",
            description: "Strategic media buying and campaign planning",
            features: ["Media Strategy", "Budget Planning", "Campaign Management"]
        },
        { 
            icon: <Zap size={24} />, 
            title: "Digital Marketing", 
            color: "from-emerald-500 to-teal-600",
            description: "End-to-end digital presence and promotion",
            features: ["SEO Strategy", "Social Media", "Analytics"]
        },
    ];

    const processSteps = [
        { step: "01", title: "Discovery", description: "Understanding your vision & goals" },
        { step: "02", title: "Concept", description: "Creative ideation & scripting" },
        { step: "03", title: "Production", description: "Professional shooting & direction" },
        { step: "04", title: "Post-Production", description: "Editing, VFX & sound design" }
    ];

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white py-16 md:py-24 relative overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute top-20 left-0 w-72 h-72 bg-emerald-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
                <div className="absolute bottom-20 right-0 w-72 h-72 bg-green-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000" />
            </div>

            <div className="max-w-7xl mx-auto px-4">
                {/* Header Section */}
                <div className="text-center max-w-3xl mx-auto mb-16 relative">
                    {/* Floating Icons */}
                    <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 flex gap-4 opacity-20">
                        <Film size={40} className="text-emerald-600" />
                        <Camera size={40} className="text-green-600" />
                        <Video size={40} className="text-emerald-600" />
                    </div>
                    
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
                        <Sparkles size={16} />
                        Our Services
                        <Sparkles size={16} />
                    </span>
                    
                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                        Transforming Ideas Into{' '}
                        <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                            Visual Masterpieces
                        </span>
                    </h2>
                    
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        From concept to delivery, we handle every aspect of video production with creative excellence 
                        and technical expertise. Your story deserves to be told beautifully.
                    </p>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-20">
                    {services.map((service, index) => (
                        <div 
                            key={index}
                            className="group relative bg-white rounded-2xl border border-gray-100 p-6 hover:border-emerald-200 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
                        >
                            {/* Gradient Background on Hover */}
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                            
                            {/* Icon with Animated Background */}
                            <div className="relative mb-4">
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${service.color} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                                    <div className="text-white">{service.icon}</div>
                                </div>
                                
                                {/* Badge for Popular Services */}
                                {index < 4 && (
                                    <span className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs px-2 py-1 rounded-full shadow-lg animate-pulse">
                                        Popular
                                    </span>
                                )}
                            </div>

                            {/* Content */}
                            <h3 className="font-bold text-xl text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                                {service.title}
                            </h3>
                            
                            <p className="text-gray-600 text-sm mb-4">
                                {service.description}
                            </p>

                            {/* Features List */}
                            <ul className="space-y-2 mb-4">
                                {service.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-center gap-2 text-xs text-gray-500">
                                        <CheckCircle size={12} className="text-emerald-500 flex-shrink-0" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* Hover Indicator Line */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-green-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-b-2xl" />
                        </div>
                    ))}
                </div>

                {/* Process Section */}
                <div className="mb-20">
                    <div className="text-center mb-12">
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                            Our <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">Creative Process</span>
                        </h3>
                        <p className="text-gray-600">A systematic approach to bring your vision to life</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {processSteps.map((step, index) => (
                            <div key={index} className="relative group">
                                {/* Connector Line */}
                                {index < processSteps.length - 1 && (
                                    <div className="hidden lg:block absolute top-1/4 left-full w-full h-0.5 bg-gradient-to-r from-emerald-200 to-green-200 -z-10 transform -translate-y-1/2">
                                        <ArrowRight size={20} className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 text-emerald-400" />
                                    </div>
                                )}
                                
                                <div className="bg-white rounded-2xl border border-gray-100 p-6 hover:border-emerald-200 transition-all hover:shadow-xl group-hover:-translate-y-1 duration-300">
                                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                        <span className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                                            {step.step}
                                        </span>
                                    </div>
                                    <h4 className="font-bold text-gray-900 mb-2">{step.title}</h4>
                                    <p className="text-sm text-gray-600">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Why Choose Us Section */}
                <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-8 -mt-8" />
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-black/10 rounded-full -ml-8 -mb-8" />
                    
                    <div className="relative z-10">
                        <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                            Why Choose Kaanshi Films?
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center group hover:transform hover:scale-105 transition-all">
                                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm group-hover:bg-white/30 transition-all">
                                    <Star size={32} className="text-white" />
                                </div>
                                <h4 className="font-semibold text-lg mb-2">Creative Excellence</h4>
                                <p className="text-sm text-white/80">Award-winning creative team with years of industry experience</p>
                            </div>
                            
                            <div className="text-center group hover:transform hover:scale-105 transition-all">
                                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm group-hover:bg-white/30 transition-all">
                                    <Users size={32} className="text-white" />
                                </div>
                                <h4 className="font-semibold text-lg mb-2">Dedicated Team</h4>
                                <p className="text-sm text-white/80">End-to-end support from concept to final delivery</p>
                            </div>
                            
                            <div className="text-center group hover:transform hover:scale-105 transition-all">
                                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm group-hover:bg-white/30 transition-all">
                                    <Heart size={32} className="text-white" />
                                </div>
                                <h4 className="font-semibold text-lg mb-2">Client First</h4>
                                <p className="text-sm text-white/80">Your vision and satisfaction are our top priorities</p>
                            </div>
                        </div>
                        
                        <div className="text-center mt-10">
                            <button className="inline-flex items-center gap-2 bg-white text-emerald-700 px-8 py-3 rounded-full font-semibold hover:bg-emerald-50 transition-all hover:scale-105 shadow-lg">
                                Start Your Project
                                <ArrowRight size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ServicesSection;